import { Alert, Box, Button, Divider, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchLatestBidEventsByStationAndDate } from "../../api/bid";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice";
import { BID_DATE_TYPE, BID_STATUS } from "../../util/bid/bidConstants";
import DashboardAdminBid from "./DashboardAdminBid";
import { selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice";
import { mergeWebSocketAdminBids } from "../../util/webSocketUtils";
import { getScrubbedBidEvents } from "../../util/bid/bidScrubber";

const DashboardAdminBidsCard = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [bidData, setBidData] = useState([]);

  const isFirstLoadRef = useRef(true);
  const bidWebSocketDataRef = useRef({});

  useEffect(() => {
    fetchBidData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter, props.workgroupFilters]);

  useEffect(() => {
      bidWebSocketDataRef.current = webSocketEventData.bidAdminEvents;
      if (Object.values(webSocketEventData.bidAdminEvents).length > 0) {
        if (!isFirstLoadRef.current) {
          setBidData((prevState) => {
            return mergeWebSocketAdminBids(webSocketEventData.bidAdminEvents, prevState);
          });
          bidWebSocketDataRef.current = {};
        }
      }
      isFirstLoadRef.current = false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webSocketEventData.bidAdminEvents]);

  const fetchBidData = async () => {
    try {
      setIsLoading(true);
      const now = DateTime.now();
      const twoMonthsFromNow = now.plus({ months: 2 });
      const response = await fetchLatestBidEventsByStationAndDate(
        userSession.eid,
        stationFilter,
        BID_DATE_TYPE.BID_EFFECTIVE_DATE,
        now,
        twoMonthsFromNow
      );
      const data = await response.json();
      let scrubbedBidEvents = getScrubbedBidEvents(data.events);
      scrubbedBidEvents = mergeWebSocketAdminBids(bidWebSocketDataRef.current, scrubbedBidEvents);
      bidWebSocketDataRef.current = {};
      setBidData(scrubbedBidEvents.filter((bidEvent) => props.workgroupFilters.includes(bidEvent.workgroup)) || []);
    } catch (error) {
      console.error("Error fetching bid data:", error);
      setFetchError("Unexpected Error");
    } finally {
      setIsLoading(false);
    }
  };

  const closedBids = bidData.filter((bid) => bid.status === BID_STATUS.CLOSED) || [];
  const openBids = bidData.filter((bid) => bid.status === BID_STATUS.OPEN) || [];
  const pendingBids =
    bidData.filter(
      (bid) => bid.status === BID_STATUS.PENDING && bid.schedStartDate !== undefined && bid.schedEndDate !== undefined
    ) || [];

  return (
    <Paper
      variant="outlined"
      sx={{ backgroundColor: "secondary.swaGray1", borderRadius: "5px", display: "flex", flexDirection: "column" }}
    >
      <Grid
        container
        sx={{
          textAlign: "left",
          paddingLeft: "1.5rem",
          paddingTop: "1rem",
          paddingRight: "1.5rem",
          paddingBottom: ".5rem",
        }}
      >
        <Grid item xs={6}>
          <Typography variant="h6" component="div" style={{ textAlign: "left", fontWeight: "bold" }}>
            All Bids
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "end" }}>
          <Button href={`#/bidAdministration`} size="small">
            <Typography sx={{ color: "primary.swaBlue", textDecoration: "underline" }}>See All</Typography>
            <Typography sx={{ color: "primary.swaBlue" }}>→</Typography>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      {fetchError !== "" ? (
        <Alert
          variant="filled"
          severity="error"
          sx={{ display: "flex", width: "200px", alignSelf: "center", margin: "1rem" }}
        >
          {fetchError}
        </Alert>
      ) : (
        //   TODO: Potentially add alerts section for bids which are close to expiring (effective end date is < 1 week away and no bid is set up yet for that workgroup)
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "300px" }}>
            <Typography
              fontWeight="bold"
              sx={{ fontSize: "15px", marginBottom: "10px", textAlign: "left", marginLeft: "30px" }}
            >
              CLOSED BIDS
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                paddingLeft: "2rem",
                paddingRight: "2rem",
              }}
            >
              {isLoading ? (
                <Skeleton height={120} />
              ) : closedBids.length > 0 ? (
                closedBids.map((item) => <DashboardAdminBid key={item.referenceNumber} bid={item} />)
              ) : (
                <Typography sx={{ textAlign: "center", padding: 2 }}>No closed bids available</Typography>
              )}
            </Box>
          </Box>
          <Divider sx={{ margin: "10px 20px" }} />
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "300px" }}>
            <Typography
              fontWeight="bold"
              sx={{ fontSize: "15px", marginBottom: "10px", textAlign: "left", marginLeft: "30px" }}
            >
              OPEN BIDS
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                paddingLeft: "2rem",
                paddingRight: "2rem",
              }}
            >
              {isLoading ? (
                <Skeleton height={120} />
              ) : openBids.length > 0 ? (
                openBids.map((item) => <DashboardAdminBid key={item.referenceNumber} bid={item} />)
              ) : (
                <Typography sx={{ textAlign: "center", padding: 2 }}>No open bids available</Typography>
              )}
            </Box>
          </Box>
          <Divider sx={{ margin: "10px 20px" }} />
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, maxHeight: "300px" }}>
            <Typography
              fontWeight="bold"
              sx={{ fontSize: "15px", marginBottom: "10px", textAlign: "left", marginLeft: "30px" }}
            >
              PENDING BIDS
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                paddingLeft: "2rem",
                paddingRight: "2rem",
              }}
            >
              {isLoading ? (
                <Skeleton height={120} />
              ) : pendingBids.length > 0 ? (
                pendingBids.map((item) => <DashboardAdminBid key={item.referenceNumber} bid={item} />)
              ) : (
                <Typography sx={{ textAlign: "center", padding: 2 }}>No pending bids available</Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

DashboardAdminBidsCard.propTypes = {
  workgroupFilters: PropTypes.array.isRequired,
};

export default DashboardAdminBidsCard;
